import React, { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import axios from "axios";
import LoadingOverlay from "../common/LoadingOverlay/LoadingOverlay";

import { Button } from "@mui/material";
import { useParams } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import { getImageLimit, formatImageLimit } from "../../utils/imageLimit";
import { getAuthToken } from "../../utils/authStorage";


import "./SelectImages.css";

function SelectImages() {
  const [selectedImageIds, setSelectedImageIds] = useState([]); // UUIDs

  const [currentPage, setCurrentPage] = useState(0);
  const [imagesPerPage, setImagesPerPage] = useState(6); // 3 x 2 como el mock
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeText, setAnalyzeText] = useState("Analyzing...");
  const navigate = useNavigate();
  const location = useLocation();
  const [isPressed, setIsPressed] = useState(false);
  const [images, setImages] = useState([]);
  const { inspectionId: inspectionIdParam } = useParams();
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [limitError, setLimitError] = useState("");
  const { user, loading: userLoading, isViewer } = useUser();
  const imageLimit = getImageLimit(user);

  // Lee los datos del estado de navegación de forma segura.
  // Si 'location.state' es nulo, 'files' será un array vacío por defecto.
  const {
    projectId,
    projectName,
    inspectionName,
    createdBy,
    createdAt,
    inspectionId: inspectionIdState,
    kmzFile,
    inspectionDomain,
    imageModality,
  } = location.state || {};

  const inspectionId = inspectionIdState || inspectionIdParam;
  const isThermalInspection = inspectionDomain === "SOLAR_PANEL" || imageModality === "THERMAL";

useEffect(() => {
  if (isViewer) return;
  if (!inspectionId) return;

  const fetchImages = async () => {
    setIsLoadingImages(true);
    try {
      const token = getAuthToken();
      const res = await axios.get(
        `/api/inspections/${inspectionId}/images`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setImages(res.data.images || []);
    } catch (e) {
      console.error("Error loading images", e);
    } finally {
      setIsLoadingImages(false);
    }
  };

  fetchImages();
}, [inspectionId, isViewer]);

  // helpers
  const totalPages = Math.max(1, Math.ceil(images.length / imagesPerPage));
  const startIndex = currentPage * imagesPerPage;
  const endIndex = Math.min(startIndex + imagesPerPage, images.length);

  const displayedImages = useMemo(
    () => images.slice(startIndex, endIndex),
    [images, startIndex, endIndex],
  );

  console.log(displayedImages);

  const handleToggle = (imageId) => {
    if (userLoading) {
      setLimitError("Loading your image limit. Please try again in a moment.");
      return;
    }
    if (!selectedImageIds.includes(imageId) && imageLimit != null && selectedImageIds.length >= imageLimit) {
      setLimitError(`You can select up to ${imageLimit} image${imageLimit === 1 ? "" : "s"}.`);
      return;
    }
    setLimitError("");
    setSelectedImageIds((prev) =>
      prev.includes(imageId)
        ? prev.filter((id) => id !== imageId)
        : [...prev, imageId],
    );
  };

  // Select/Deselect solo la página visible (como en el mock)
  const handleSelectAllVisible = () => {
    if (userLoading) {
      setLimitError("Loading your image limit. Please try again in a moment.");
      return;
    }

    const allIds = images.map((img) => img.id);
    const allSelected =
      allIds.length > 0 && allIds.every((id) => selectedImageIds.includes(id));
    if (allSelected) {
      setLimitError("");
      setSelectedImageIds([]);
      return;
    }

    const limitedIds = imageLimit == null ? allIds : allIds.slice(0, imageLimit);
    setSelectedImageIds(limitedIds);
    setLimitError(
      limitedIds.length < allIds.length
        ? `Only ${limitedIds.length} image${limitedIds.length === 1 ? "" : "s"} selected. ${formatImageLimit(imageLimit)}.`
        : "",
    );
  };

  const goToPage = (p) =>
    setCurrentPage(Math.min(Math.max(0, p), totalPages - 1));
  const isValidUUID = (v) =>
    typeof v === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      v || "",
    );

  const mergeAnalysis = (acc, next) => {
    if (!acc) return next;

    const accResults = Array.isArray(acc.results) ? acc.results : [];
    const nextResults = Array.isArray(next.results) ? next.results : [];

    // Dedup por image_id (o image_name si hiciera falta)
    const map = new Map();
    for (const r of accResults) map.set(r.image_id ?? r.image_name ?? r.id, r);
    for (const r of nextResults) map.set(r.image_id ?? r.image_name ?? r.id, r);

    return {
      ...acc,
      ...next, // deja que el “último” actualice campos globales si existen
      results: Array.from(map.values()),
    };
  };

  const chunkArray = (arr, size) => {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  };

  const analyzeImages = async () => {
    if (!projectId || !inspectionId) return;
    if (!selectedImageIds || selectedImageIds.length === 0) return;
    if (userLoading) {
      setLimitError("Loading your image limit. Please try again in a moment.");
      return;
    }
    if (imageLimit != null && selectedImageIds.length > imageLimit) {
      setLimitError(`You can analyze up to ${imageLimit} image${imageLimit === 1 ? "" : "s"}.`);
      return;
    }

    setIsAnalyzing(true);
    const analysisImageIds = [...selectedImageIds];
    setAnalyzeText(
      isThermalInspection
        ? "Preparing thermal results..."
        : `Analyzing... 0 of ${analysisImageIds.length}`,
    );

    const selectedImagesDTO = images
      .filter((img) => analysisImageIds.includes(img.id))
      .map((img) => ({
        ...img,
        originalUrl: img.signedUrl || img.imageUrl || img.url || null,
        name: img.imageName || img.name || "",
      }));

    try {
      if (isThermalInspection) {
        navigate(`/inspections/${inspectionId}/results`, {
          state: {
            inspectionId,
            projectId,
            selectedImages: selectedImagesDTO,
            projectName,
            inspectionName,
            createdBy,
            createdAt,
            kmzFile,
            inspectionDomain,
            imageModality,
            thermalMode: true,
          },
        });
        return;
      }

      const analysisImageIdSet = new Set(analysisImageIds.map(String));
      const initiallyAnalyzedCount = images.filter(
        (img) => img.analysed && analysisImageIdSet.has(String(img.id)),
      ).length;
      const token = getAuthToken();

      const requestState = { finished: false, error: null };
      const predictionRequest = axios.post(
        `/api/predict`,
        { projectId, inspectionId, imageIds: analysisImageIds },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          timeout: 1000 * 60 * 30,
        },
      ).then(() => {
        requestState.finished = true;
      }).catch((error) => {
        requestState.error = error;
      });

      // The prediction request stays open while the backend works. Poll in
      // parallel and count only DONE results from this selected batch.
      const total = analysisImageIds.length;
      await new Promise((resolve, reject) => {
        let observedPendingReset = initiallyAnalyzedCount === 0;

        const poll = async () => {
          if (requestState.error) {
            reject(requestState.error);
            return;
          }

          try {
            const res = await axios.get(`/api/${inspectionId}/predictions`, {
              headers: { Authorization: `Bearer ${token}` },
            });

            const data = res.data?.body ?? res.data ?? {};
            const resultsArr = Array.isArray(data.results)
              ? data.results
              : Array.isArray(data)
                ? data
                : [];

            const completedSelectedIds = new Set(
              resultsArr
                .map((result) => result.image_id ?? result.imageId)
                .filter((imageId) => imageId != null)
                .map(String)
                .filter((imageId) => analysisImageIdSet.has(imageId)),
            );

            if (completedSelectedIds.size < initiallyAnalyzedCount) {
              observedPendingReset = true;
            }

            const processed =
              observedPendingReset || requestState.finished
                ? completedSelectedIds.size
                : 0;
            const done = requestState.finished;

            setAnalyzeText(`Analyzing... ${processed} of ${total}`);
            if (done) {
              resolve();
            } else {
              setTimeout(poll, 3000);
            }
          } catch {
            // keep polling on transient errors
            setTimeout(poll, 4000);
          }
        };
        poll();
      });
      await predictionRequest;

      navigate(`/inspections/${inspectionId}/results`, {
        state: {
          inspectionId,
          projectId,
          selectedImages: selectedImagesDTO,
          projectName,
          inspectionName,
          createdBy,
          createdAt,
          kmzFile,
          inspectionDomain,
          imageModality,
        },
      });
    } catch (error) {
      console.error("Error analyzing images:", error);
      setLimitError(
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.response?.data ||
        "Could not analyze the selected images.",
      );
    } finally {
      setIsAnalyzing(false);
      setAnalyzeText(isThermalInspection ? "Preparing thermal results..." : "Analyzing...");
    }
  };

  const analysedCount = images.filter((img) => img.analysed).length;

  if (isViewer) {
    return (
      <div className="si-page">
        <div className="si-card-like">
          <h2>Analysis unavailable</h2>
          <p>This viewer profile can only open inspections that already have results.</p>
          <button className="si-primary" type="button" onClick={() => navigate("/projects")}>
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="si-page">
      {/* Banner de resultados parciales */}
      {analysedCount > 0 && (
        <div className="si-banner">
          <span>
            {analysedCount} of {images.length} images already analysed.
          </span>
          <button
            className="si-banner-link"
            onClick={() =>
              navigate(`/inspections/${inspectionId}/results`, {
                state: {
                  inspectionId,
                  projectId,
                  selectedImages: images
                    .filter((img) => img.analysed)
                    .map((img) => ({
                      ...img,
                      originalUrl: img.signedUrl || img.imageUrl || img.url || null,
                      name: img.imageName || img.name || "",
                    })),
                  projectName,
                  inspectionName,
                  createdBy,
                  createdAt,
                  kmzFile,
                  inspectionDomain,
                  imageModality,
                },
              })
            }
          >
            View results →
          </button>
        </div>
      )}

      {/* Header */}
      <div className="si-header">
        <div>
          <h1 className="si-title">Uploaded Images</h1>
          <p className="si-subtitle">
            {selectedImageIds.length}/{images.length} Images Selected
            {imageLimit != null ? ` · ${formatImageLimit(imageLimit)}` : ""}
            {isThermalInspection ? " · Thermal" : ""}
          </p>
        </div>

        <button
          type="button"
          className="si-select-all"
          onClick={handleSelectAllVisible}
          disabled={userLoading}
        >
          Select All Images
        </button>
      </div>
      {limitError && <div className="si-limit-error">{limitError}</div>}

      {/* Grid */}
      <div className="si-grid">
        {displayedImages.map((img) => {
          const selected = selectedImageIds.includes(img.id);
          const src = window.__toAbsoluteUrl(img.signedUrl || img.imageUrl || img.url);

          return (
            <ImageCard
              key={img.id}
              src={src}
              title={(img.imageName || "Image").replace(/\.[^/.]+$/, "")}
              updatedText={img.createdAt ? `Updated at ${img.createdAt}` : ""}
              selected={selected}
              analysed={img.analysed}
              onClick={() => handleToggle(img.id)}
              onView={() => handleToggle(img.id)}
              onDelete={() => {}}
            />
          );
        })}
      </div>

      {/* Paginación */}
      <div className="si-pagination">
        <button
          className="si-page-btn"
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 0}
        >
          ‹
        </button>
        {Array.from({ length: totalPages }).map((_, i) => (
          <button
            key={i}
            className={`si-page-btn ${i === currentPage ? "is-active" : ""}`}
            onClick={() => goToPage(i)}
          >
            {i + 1}
          </button>
        ))}
        <button
          className="si-page-btn"
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages - 1}
        >
          ›
        </button>
      </div>

      {/* Footer CTA */}
      <div className="si-footer">
        <Button
          className="si-cta"
          variant="contained"
          onClick={analyzeImages}
          disabled={userLoading || selectedImageIds.length === 0 || (imageLimit != null && selectedImageIds.length > imageLimit)}
        >
          {isThermalInspection ? "View" : "Analyze"} {selectedImageIds.length} Selected Image
          {selectedImageIds.length === 1 ? "" : "s"}
        </Button>
      </div>

      <LoadingOverlay show={isAnalyzing || isLoadingImages} text={isAnalyzing ? analyzeText : "Loading images..."} />
    </div>
  );
}

/** ---------- Card interna estilo mock ---------- */
function ImageCard({
  src,
  title,
  updatedText,
  selected,
  analysed,
  onClick,
  onView,
  onDelete,
  onRevoke,
}) {
  return (
    <div
      className={`si-card ${selected ? "is-selected" : ""} ${analysed ? "is-analysed" : ""}`}
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}
    >
      {selected && (
        <div className="si-check">
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
            <path
              d="M20 6L9 17l-5-5"
              fill="none"
              stroke="white"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}
      {analysed && !selected && (
        <div className="si-analysed-badge" title="Already analysed">
          ✓
        </div>
      )}
      <div className="si-thumb">
        <img
          src={src}
          alt={title}
          onLoad={() => {
            /* listo */
          }}
          onError={() => {}}
        />
      </div>
      <div className="si-card-footer" onClick={(e) => e.stopPropagation()}>
        <div className="si-card-info">
          <div className="si-card-title">{title}</div>
          <div className="si-card-meta">{updatedText}</div>
        </div>
        <div className="si-card-actions">
          <button className="si-chip" onClick={onView}>
            👁️ View Details
          </button>
          <button className="si-danger" onClick={onDelete}>
            🗑️ Delete Now
          </button>
        </div>
      </div>
      {/* revocar objectURL al desmontar */}
      <Unmount onUnmount={onRevoke} />
    </div>
  );
}

/** tiny helper para ejecutar side-effects al desmontar */
function Unmount({ onUnmount }) {
  React.useEffect(
    () => () => {
      try {
        onUnmount && onUnmount();
      } catch (e) {}
    },
    [onUnmount],
  );
  return null;
}

const uploadToGCS = async (file, uploadUrl) => {
  await axios.put(uploadUrl, file, {
    headers: {
      "Content-Type": file.type,
    },
  });
};

export default SelectImages;
